import React, {useEffect, useRef, useState} from "react";
import Search from "./Search.jsx";
import {ButtonToolbar, Col, Container, Dropdown, Row, Table} from "react-bootstrap";
import HeaderRow from "./Table/HeaderRow.jsx";
import TableRow from "./Table/TableRow.jsx";
import InertiaPagination from "./InertiaPagination.jsx";
import {useLaravelReactI18n} from "laravel-react-i18n";
import {router, usePage} from "@inertiajs/react";
import '../scss/inertiaTable.scss';

export default function InertiaTable({
                                         data,
                                         title,
                                         hiddenCols,
                                         onRowClick,
                                         selectedIds,
                                         onSelectedIdsChange,
                                         customToolbarButtons,
                                         onChildRowContent,
                                         childRowContent,
                                         childLoadingPlaceholder,
                                         sortMode,
                                         options = {},
                                         customRowStyle,
                                         customRowClass,
                                         customCellRendererMap,
                                         dataBag,
                                         innerRef,
                                         actions,
                                         actionsDisplay = 'overlay',
                                         ...rest
                                     }) {
    const {t, getLocales} = useLaravelReactI18n();
    const {filters, col_definitions} = dataBag ?? usePage().props;
    const [search, setSearch] = useState(filters.search);
    const [tempData, setTempData] = useState(data.data);
    const item_count = data.total;
    const itemsPerPageOptions = [5, 10, 25, 50, 100, 500, 1000]; // Optionen für Artikel pro Seite
    const dragItem = useRef();
    const dragOverItem = useRef();
    const [initialLoad, setInitialLoad] = useState(true);
    const [firstSelectedRowIndex, setFirstSelectedRowIndex] = useState(null);
    const [isShiftKeyPressed, setIsShiftKeyPressed] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [childRowIndex, setChildRowIndex] = useState(-1);
    const [childRowContentLoading, setChildRowContentLoading] = useState(true);
    // Standardwert für showCheckbox, falls nicht gesetzt
    const showCheckbox = (options.selectable && options.showCheckbox) ?? false;
    const childLoadingPlaceholderElement = childLoadingPlaceholder ?? (<div>Loading...</div>);

    // Check if the array colDefinitions contains an element with "key" attribute set to true
    const keyElement = col_definitions.find(col => col.key === true);
// Save the id field of the element into keyField, else set it to "id"
    const keyField = keyElement ? keyElement.id : 'id';


    useEffect(() => {
        setTempData(data.data);
    }, [data]);

    useEffect(() => {
        setChildRowContentLoading(false);
    }, [childRowContent]);

    useEffect(() => {
        if (!initialLoad) {
            router.reload({data: {sort_mode: sortMode}});
        } else {
            setInitialLoad(false);
        }
    }, [sortMode]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Shift') {
                setIsShiftKeyPressed(true);
            }
        };
        const handleKeyUp = (event) => {
            if (event.key === 'Shift') {
                setIsShiftKeyPressed(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const onItemsPerPageChange = (count) => {
        const reloadOptions = {data: {filter: filters, 'items_per_page': count, 'page': data.current_page}};
        if (options?.updateOnly) {
            reloadOptions.only = [...['filters'], ...options.updateOnly];
        }
        router.reload(reloadOptions);
    };

    const onSearchChange = (value) => {
        if (value === '') {
            setSearch(value);
            router.reload({data: {'search': null, 'page': 1}});
        } else {
            setSearch(value);
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        router.reload({data: {'search': search, 'page': 1}});
    };

    const handleOnRowClick = (row, index) => {
        if (onRowClick!=null && typeof onRowClick === "function") {
            onRowClick(row);
        } else if (options?.selectable) {
            if (isShiftKeyPressed && firstSelectedRowIndex !== null) {
                const rangeStart = Math.min(firstSelectedRowIndex, index);
                const rangeEnd = Math.max(firstSelectedRowIndex, index);
                const newSelectedIds = new Set(selectedIds);
                for (let i = rangeStart; i <= rangeEnd; i++) {
                    newSelectedIds.add(tempData[i].id);
                }
                onSelectedIdsChange(Array.from(newSelectedIds));
            } else {
                if (!selectedIds.includes(row.id)) {
                    onSelectedIdsChange(prevState => [...prevState, row.id]);
                } else {
                    onSelectedIdsChange(prevState => selectedIds.filter(id => id !== row.id));
                }
                setFirstSelectedRowIndex(index);
            }
        }
    };

    const handleOnRowSelect = (row, index, state) => {
        if (state) {
            onSelectedIdsChange(prevState => [...prevState, row.id]);
        } else {
            onSelectedIdsChange(prevState => selectedIds.filter(id => id !== row.id));
        }
    }

    let dragStartIndex;
    let lastdragOverElement;
    let dragStartId;

    const onDragStart = (e, index) => {
        e.target.classList.add("dragging");
        const parentNode = e.target.parentNode;
        const children = Array.from(parentNode.children);
        dragStartIndex = children.indexOf(e.target);
        dragStartId = e.target.dataset.id;
    };

    const handleSelectAllChange = (state) => {
        setSelectAll(state);
        if (state) {
            onSelectedIdsChange(tempData.map(item => item.id));
        } else {
            onSelectedIdsChange([]);
        }
    };

    const handleOnChildRowStateChange = (id) => {
        // Speichern welches Element geöffnet ist
        setChildRowIndex(id);
        // Update des Contents anfragen
        onChildRowContent(id);
        if (id !== -1) {
            setChildRowContentLoading(true);
        }
    }

    const reorderTempData = (srcIndex, destIndex) => {
        const updatedData = Array.from(tempData);
        const [movedItem] = updatedData.splice(srcIndex, 1);
        updatedData.splice(destIndex, 0, movedItem);
        setTempData(updatedData);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        if (e.target === lastdragOverElement) {
            return;
        }
        lastdragOverElement = e.target;
        const draggingElement = document.querySelector(".dragging");
        const parentNode = draggingElement.parentNode;
        const targetElement = e.target.parentNode;
        const children = Array.from(parentNode.children);
        const dragEndIndex = children.indexOf(targetElement); // parentNode weil event vom td kommt, nicht vom tr
        draggingElement.style.visibility = 'hidden';
        if (dragEndIndex !== dragStartIndex) {
            if (dragEndIndex > dragStartIndex) {
                parentNode.insertBefore(draggingElement, targetElement.nextSibling);
            } else {
                parentNode.insertBefore(draggingElement, targetElement);
            }
        }
    };

    const onDragEnd = (e) => {
        e.preventDefault();
        e.target.style.visibility = null;
        e.target.classList.remove("dragging");
        let parent = e.target.parentNode;
        let children = Array.prototype.slice.call(parent.children);
        let targetId = children.indexOf(e.target);
        const sourceIndex = dragStartId;
        const targetIndex = Array.from(e.target.parentNode.children).indexOf(e.target);
        onRowReorder(dragStartId, targetIndex);
    };

    const onRowReorder = (source, target) => {
        router.reload({data: {reorder: {source: tempData[source].id, target: tempData[target].id}}});
//    router.reload({ only: ['status'], data: { reorder: { source: tempData[source].id, target: tempData[target].id } } });
    };

    const onEnumSelect = (id, key) => {
        event.preventDefault();
        let reloadData = {'search': search, 'page': data.current_page};

        console.log("onEnumSelect: ", id, " - ", key, " -");
        if (key !== "-1") {
            // Füge den neuen Wert hinzu, ohne bestehende Einträge zu ersetzen
            reloadData = {
                ...reloadData,
                filter: {
                    ...filters,
                    enums: {
                        ...filters.enums, // Bestehende Werte beibehalten
                        [id]: key,        // Neuen Wert hinzufügen
                    },
                },
            };
        } else {
            // Entferne den Eintrag, indem `undefined` gesetzt wird, aber auch bestehende Werte beibehalten
            const { [id]: _, ...remainingEnums } = filters.enums; // Entferne den Eintrag für die ID
            reloadData = {
                ...reloadData,
                filter: {
                    ...filters,
                    enums: remainingEnums,
                },
            };
        }


        let reloadOptions = {data: reloadData};
        if (options?.updateOnly) {
            reloadOptions.only = [...['filters'], ...options.updateOnly];
        }
        router.reload(reloadOptions);
    };

    const handleSort = (id, direction) => {
        event.preventDefault();
        let reloadOptions = {
            data: {
                filter: {...filters, 'sort': id, 'order_direction': direction},
                'page': data.current_page
            }
        };

//    let reloadOptions = { data: { 'search': search,'page': data.current_page } };
        if (options?.updateOnly) {
            reloadOptions.only = [...['filters'], ...options.updateOnly];
        }
        router.reload(reloadOptions);
    };

    return (
        <>
            <Container fluid className={"h-100 pt-2 d-flex flex-column"}>
                {title && <div className="mt-1 mb-3 d-flex">
                    <h1>{title}</h1>
                </div>}
                <Row>
                    <Col>
                        <ButtonToolbar>
                            <Dropdown className="me-2" onSelect={onItemsPerPageChange}>
                                <Dropdown.Toggle variant="secondary" id="dropdown-items-per-page">
                                    {data.per_page}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {itemsPerPageOptions.map((option) => (
                                        <Dropdown.Item eventKey={option} key={option}>{option}</Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                            {customToolbarButtons && customToolbarButtons}
                        </ButtonToolbar>
                    </Col>
                    <Col md={3}>
                        <Search disabled={sortMode} placeholder={t('search')} onChange={(e) => onSearchChange(e.target.value)}
                                onSubmit={(e) => handleSubmit(e)} value={search}>{t('search')}</Search>
                    </Col>
                </Row>

                <div className="table-responsive table-container flex-grow-1">
                    <Table {...rest} ref={innerRef} className={'inertia-table'} striped={options?.striped} bordered hover>
                        <thead>
                        <HeaderRow
                            disabled={sortMode}
                            selectable={options?.selectable}
                            showCheckbox={showCheckbox} // Veränderte Zeile
                            onSelectAllChange={handleSelectAllChange}
                            selectAll={selectAll}
                            onSort={handleSort}
                            onEnumSelect={onEnumSelect}
                            className="bg-gray-100"
                            sortCol={filters.sort}
                            direction={filters.order_direction}
                            enumFilter={filters.enum_filter}
                            enumSelected={filters.enum_key}
                            enums={filters.enums}
                            cols={col_definitions}
                            hiddenCols={hiddenCols}
                            actions={actions}
                            actionsDisplay={actionsDisplay}
                        />
                        </thead>
                        <tbody>
                        {tempData.map((row, index) => (
                            <TableRow
                                key={row.id}
                                draggable={sortMode}
                                selectable={options?.selectable}
                                showCheckbox={showCheckbox} // Veränderte Zeile
                                selected={selectedIds?.includes(row.id)}
                                onRowClick={options?.selectable || onRowClick ? () => handleOnRowClick(row, index) : undefined}                onRowSelect={(state) => handleOnRowSelect(row, index, state)}
                                onDragStart={onDragStart}
                                onDragEnd={onDragEnd}
                                onDragOver={onDragOver}
                                data={row}
                                index={index}
                                childRows={options.childRows}
                                childRowContent={childRowContentLoading ? childLoadingPlaceholderElement : childRowContent}
                                childRowState={childRowIndex === row[keyField]}
                                onChildRowStateChange={handleOnChildRowStateChange}
                                colDefinitions={col_definitions}
                                customRowStyle={customRowStyle}
                                customRowClass={customRowClass}
                                customCellRendererMap={customCellRendererMap}
                                hiddenCols={hiddenCols}
                                actions={actions}
                                actionsDisplay={actionsDisplay}
                            />
                        ))}
                        </tbody>
                    </Table>
                </div>
                <Row className="align-items-center mt-2">
                    <Col xs="auto" className="d-flex align-items-center pb-3">
                        {item_count} {t('items')}
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center flex-grow-1">
                        <InertiaPagination updateOnly={options?.updateOnly} links={data.links} />
                    </Col>
                </Row>

            </Container>
        </>
    );
}
